import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer, CheckCircle, Bluetooth } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'sonner';
import { escposPrinter } from '../utils/escpos';

export default function FnBPrintDialog({ order, open, onClose }) {
  const printRef = useRef(null);
  const [printing, setPrinting] = useState(false);
  const [savedPrinter, setSavedPrinter] = useState(escposPrinter.getSavedPrinterName());
  const [businessProfile, setBusinessProfile] = useState(null);
  const [orderTimestamp] = useState(new Date()); // FIXED timestamp saat modal dibuka
  const [branchInfo, setBranchInfo] = useState(null);

  // Fetch business profile and branch info
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // Fetch business profile
        const profileResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/business-profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const profileData = await profileResponse.json();
        if (profileData.success && profileData.profile) {
          setBusinessProfile(profileData.profile);
        }
        
        // Fetch branch name from order.branch_id
        if (order?.branch_id) {
          const branchResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branches`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const branchData = await branchResponse.json();
          if (branchData.success && branchData.branches) {
            const branch = branchData.branches.find(b => b.branch_id === order.branch_id);
            if (branch) {
              setBranchInfo(branch.name);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    if (open && order) {
      fetchData();
    }
  }, [open, order]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });

  const onPrintClick = () => {
    try {
      setTimeout(() => {
        handlePrint();
        toast.success('Struk F&B berhasil dicetak!');
      }, 100);
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Gagal mencetak struk');
    }
  };

  // ESC/POS Bluetooth Print
  const onBluetoothPrint = async (forceNew = false) => {
    if (!order) return;
    
    setPrinting(true);
    try {
      toast.loading('Connecting to printer...', { id: 'bt-print' });
      
      // Connect to printer
      await escposPrinter.connect(forceNew);
      
      // CRITICAL: Add extra stabilization delay after connection
      // This ensures device is fully ready before sending print commands
      console.log(' Waiting for printer stabilization...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      toast.loading('Printing...', { id: 'bt-print' });
      
      // Print receipt
      await escposPrinter.printFnBReceipt(order);
      
      // Update saved printer name
      setSavedPrinter(escposPrinter.getSavedPrinterName());
      
      toast.success(' Struk berhasil dicetak!', { id: 'bt-print' });
      
      // Keep connection alive (don't disconnect)
      
    } catch (error) {
      console.error('Bluetooth print error:', error);
      toast.error(' Gagal print: ' + error.message, { id: 'bt-print' });
    } finally {
      setPrinting(false);
    }
  };

  // Change printer
  const onChangePrinter = () => {
    escposPrinter.forgetPrinter();
    setSavedPrinter('No printer saved');
    toast.info('Printer dilupakan. Print berikutnya akan minta pilih printer baru.');
  };

  if (!open || !order) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto p-4"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full my-8 max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-pink-600 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-white" />
            <div>
              <h3 className="text-xl font-bold text-white">Pembayaran Berhasil!</h3>
              <p className="text-orange-100 text-sm mt-1">Order F&B</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* Order Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
            <div className="text-center mb-3">
              <p className="text-sm font-semibold text-gray-700">Struk F&B</p>
              <p className="text-xs text-gray-500 mt-1">{order.customer_name}</p>
            </div>
            
            {/* Detailed preview - Match print format */}
            <div className="bg-white p-4 rounded shadow-sm text-xs border space-y-2">
              <div className="text-center border-b pb-2">
                {/* Business Name - smaller font */}
                <p className="font-bold text-xs leading-tight">{businessProfile?.business_name?.toUpperCase() || 'RIMURU GAME SUITE'}</p>
                {/* Branch Name - Use state variable */}
                <p className="text-gray-700 text-xs font-bold mt-0.5">
                  {branchInfo || 'Cabang Utama'}
                </p>
                {/* Powered by */}
                <p className="text-gray-500 text-xs mt-1">Powered by Rimuru Game Suite</p>
              </div>
              
              {/* Kasir Info */}
              <div className="border-b pb-2">
                <div className="flex justify-between text-gray-600">
                  <span>Kasir:</span>
                  <span>{JSON.parse(localStorage.getItem('user') || '{}').full_name || order?.created_by_name || 'Test'}</span>
                </div>
              </div>
              
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Customer:</span>
                  <span className="font-semibold select-none">{order.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tanggal:</span>
                  <span className="font-semibold select-none">{orderTimestamp.toLocaleString('id-ID')}</span>
                </div>
              </div>
              
              <div className="border-t pt-2 space-y-1">
                <div className="text-gray-600 font-semibold">Items:</div>
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between pl-2">
                    <span className="text-gray-600">{item.quantity}x {item.name}</span>
                    <span>Rp {(item.price * item.quantity).toLocaleString('id-ID')}</span>
                  </div>
                ))}
              </div>
              
              <div className="border-t-2 border-dashed pt-2">
                <div className="flex justify-between font-bold text-sm">
                  <span>TOTAL:</span>
                  <span>Rp {order.total.toLocaleString('id-ID')}</span>
                </div>
                <div className="flex justify-between text-gray-600 mt-1">
                  <span>Metode:</span>
                  <span className="uppercase">{order.payment_method || 'TUNAI'}</span>
                </div>
              </div>
              
              <div className="text-center text-gray-500 text-xs pt-2 border-t">
                Terima kasih atas kunjungan Anda!
              </div>
            </div>
          </div>

          {/* Saved Printer Info */}
          {savedPrinter !== 'No printer saved' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bluetooth className="h-4 w-4 text-blue-600" />
                  <div>
                    <p className="text-xs text-gray-600">Printer tersimpan:</p>
                    <p className="text-sm font-semibold text-gray-800">{savedPrinter}</p>
                  </div>
                </div>
                <button
                  onClick={onChangePrinter}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Ganti
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {/* ESC/POS Bluetooth Print (RECOMMENDED) */}
            <button
              onClick={() => onBluetoothPrint(false)}
              disabled={printing}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 disabled:from-gray-400 disabled:to-gray-400 text-white font-bold py-4 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-lg"
            >
              <Bluetooth className="h-5 w-5" />
              {printing ? 'Printing...' : savedPrinter === 'No printer saved' ? 'Setup Printer & Print' : 'Print via Bluetooth'}
            </button>
            
            {/* Browser Print (Fallback) */}
            <button
              onClick={onPrintClick}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-md"
            >
              <Printer className="h-5 w-5" />
              Print via Browser
            </button>
            
            <button
              onClick={onClose}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              Tutup
            </button>
          </div>
        </div>

        {/* Hidden printable content */}
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <div ref={printRef} style={{ width: '58mm', padding: '10px', fontFamily: 'monospace', fontSize: '12px' }}>
            <div style={{ textAlign: 'center', marginBottom: '10px' }}>
              <h2 style={{ margin: '5px 0', fontSize: '16px', fontWeight: 'bold' }}>{businessProfile?.business_name?.toUpperCase() || 'RIMURU GAME SUITE'}</h2>
              <p style={{ margin: '2px 0', fontSize: '10px', fontWeight: 'bold' }}>{branchInfo || 'Cabang Utama'}</p>
              <p style={{ margin: '2px 0', fontSize: '10px' }}>Order F&B</p>
              <p style={{ margin: '2px 0', fontSize: '10px' }}>{orderTimestamp.toLocaleString('id-ID')}</p>
              <div style={{ borderBottom: '1px dashed black', margin: '8px 0' }}></div>
            </div>
            
            <div style={{ marginBottom: '10px' }}>
              <p style={{ margin: '3px 0' }}><strong>Customer:</strong> {order.customer_name}</p>
            </div>
            
            <div style={{ borderBottom: '1px dashed black', margin: '8px 0' }}></div>
            
            <div style={{ marginBottom: '10px' }}>
              {order.items.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0' }}>
                  <span>{item.quantity}x {item.name}</span>
                  <span>Rp {(item.price * item.quantity).toLocaleString('id-ID')}</span>
                </div>
              ))}
            </div>
            
            <div style={{ borderBottom: '2px solid black', margin: '8px 0' }}></div>
            
            <div style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '14px' }}>
                <span>TOTAL:</span>
                <span>Rp {order.total.toLocaleString('id-ID')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0', fontSize: '10px' }}>
                <span>Pembayaran:</span>
                <span>{order.payment_method?.toUpperCase() || 'TUNAI'}</span>
              </div>
            </div>
            
            <div style={{ borderBottom: '1px dashed black', margin: '8px 0' }}></div>
            
            <div style={{ textAlign: 'center', fontSize: '10px', marginTop: '10px' }}>
              <p>Terima kasih atas kunjungan Anda!</p>
              <p>Sampai jumpa lagi</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
